import type { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import React from 'react';
import dynamic from 'next/dynamic';
import { isEmpty, omit, pick } from 'lodash-es';
import ProductGroupApi from '@lambda/apis/src/product-group';
import {
  AvailableVariantT,
  ProductChannelEnum,
  ProductT,
  SearchProductsParamsT,
  SkuT,
} from '@lambda/apis/src/catalog-v2/types';
import CatalogV2Api from '@lambda/apis/src/catalog-v2';
import { isNotFoundError } from '@lambda/apis/src/catalog-v2/isNotFoundError';
import CategoryApi from '@lambda/apis/src/category';
import { ParsedUrlQuery } from 'querystring';
import SSM from '@lambda/commons/apis/ssm';
import { getScreenSizeFamily } from '@/lib/product/screen-size-families';
import settings from '@/settings';
import {
  AdditionalInfoT,
  ProductPageProps,
} from '@/components/v2/product/hooks/types';
import { streamlineAvailableVariants } from '@/components/v2/product/hooks/helpers';
import { findStoreLanguageSkuId } from '@/components/v2/product/sections/main/right/variants/helpers';
import { interpolateStoreTokens } from '@/lib/store-tokens';
import { packAvailableVariants } from '@/components/v2/product/hooks/variantPacking';
import { getProductReviews } from '@/lib/use-product-reviews';
import { streamlineBreadcrumb } from '@/components/collections/[handle]/NewProductPage/helpers';
import { DEPRECATED_HANDLES_MAP } from '@/components/collections-v3/hooks/helpers';
import { fetchProducts } from '@/lib/backend/collections';
import { CollectionPageProps } from '@/components/collections-v3/hooks/types';
import { ProductPageProvider } from '@/components/v2/product/hooks/useProductPageContext';
import {
  DeliveryDestinationT,
  resolveDeliveryDestination,
  trimOfferDeliveryEstimates,
} from '@/lib/delivery-estimates';
import { CollectionPageProvider } from '@/components/collections-v3/hooks/useCollectionPageContext';

const ssm = new SSM();

interface CollectionsParams {
  [key: string]: string | string[] | undefined;
}

// Both fill the screen while their chunk loads on a client-side navigation, so
// the footer doesn't jump into view and back (RT-17488).
const CollectionPageLayout = dynamic(
  () => import('@/components/collections-v3/CollectionPageLayout'),
  {
    ssr: true,
    loading: () => <div className="min-h-screen" />,
  },
);

const ProductPageLayout = dynamic(
  () => import('@/components/v2/product/ProductPageLayout'),
  {
    ssr: true,
    loading: () => <div className="min-h-screen" />,
  },
);

type Props =
  | ({ type: 'MS_CATALOG_PRODUCT_PAGE' } & ProductPageProps)
  | ({ type: 'MS_CATALOG_NAV_COLLECTION_PAGE' } & CollectionPageProps);

const REDIRECT_HOME = { redirect: { permanent: false, destination: '/' } };

// A product or collection that no longer exists is a real 404, so crawlers drop it
// instead of treating a temporary redirect home as a soft 404 (RT-17611).
const NOT_FOUND = { notFound: true } as const;

const PERMANENT_REDIRECT_ROUTE = ({ handle }: { handle: string }) => ({
  redirect: { permanent: true, destination: `/collections/${handle}` },
});

const getMsCatalogProductPageProps = async ({
  product,
  productId,
  skuId,
  catalog,
  queryParams,
  destination,
}: {
  product: ProductT;
  productId: string;
  skuId: string | null;
  catalog: CatalogV2Api;
  queryParams: ParsedUrlQuery;
  destination: DeliveryDestinationT;
}) => {
  const categoryApi = new CategoryApi();

  // Fetch SKUs
  const rawAvailableVariants = await catalog
    .getAvailableVariants(encodeURIComponent(productId))
    .catch(() => ({ items: [] }));

  // Sort Variants
  const productVariants = (product.variants ?? [])
    .sort((a, b) => a.sequence - b.sequence)
    .map((item) => ({
      ...item,
      values: item.values.slice().sort((a, b) => a.sequence - b.sequence),
    }))
    // TODO: This needs to be handled in the backend.
    .filter(
      (v) =>
        !['cellularNetwork', 'networkProvider', 'cellularStatus'].includes(
          v.variant.slug,
        ),
    );

  // Hide carrier-locked SKUs from the storefront.
  const unlockedAvailableVariants = (
    rawAvailableVariants?.items as AvailableVariantT[]
  )?.filter(
    (sku) =>
      !sku?.variants?.some(
        (v) =>
          v?.variant?.slug === 'carrier' &&
          v?.value?.value !== 'Fully Unlocked',
      ),
  );

  // Delete duplicate SKUs based on variant combination and sort by price ascending
  const streamlinedAvailableVariants = streamlineAvailableVariants(
    unlockedAvailableVariants,
    productVariants ?? [],
  );

  // Delete variant options that are not available in any of the SKUs to streamline the variant selectors on the frontend
  const streamlinedProductVariants = productVariants.map((pv) => {
    const availableValues = new Set(
      streamlinedAvailableVariants.flatMap((av) =>
        av.variants
          .filter((sv) => sv.variant.slug === pv.variant.slug)
          .map((sv) => sv.value.value),
      ),
    );

    return {
      ...pv,
      values: pv.values.filter((v) => availableValues.has(v.value)),
    };
  });

  // Streamline Query Params to only include relevant product variant filters
  const streamlinedQueryParams: Record<string, unknown> = {};

  // Streamline query params to only include those relevant to the product variants and map carrier to serviceProvider
  streamlinedProductVariants
    .map((v) => v.variant.slug)
    .forEach((slug) => {
      let value = queryParams[slug];

      // Special Case for Carrier -> serviceProvider
      if (slug === 'carrier' && !value && queryParams.serviceProvider) {
        value =
          queryParams.serviceProvider === 'Unlocked'
            ? 'Fully Unlocked'
            : queryParams.serviceProvider;
      }

      streamlinedQueryParams[slug] = value;
    });

  const getMatchingSkuId = () => {
    if (!isEmpty(streamlinedQueryParams)) {
      const queryParamsToMatchingSku = streamlinedAvailableVariants.find(
        (sku) =>
          // Check that every key/value pair in the query matches one in sku.variants
          Object.entries(streamlinedQueryParams).every(([key, value]) =>
            sku.variants.some(
              (v) => v.variant.slug === key && v.value.value === value,
            ),
          ),
      );

      if (queryParamsToMatchingSku) {
        return queryParamsToMatchingSku.skuId;
      }
    }

    // Find Matching SkuId
    let _validSkuId = skuId;

    const matchingSku = streamlinedAvailableVariants.find(
      (s) => s.skuId === _validSkuId,
    );

    if (!_validSkuId || !matchingSku) {
      // Keyboard language outranks price: a shopper shouldn't land on a foreign
      // layout, and its mismatch warning, when their own is in stock. Available
      // Variants are sorted by price ascending, so the first match is also the
      // cheapest of the matching ones — and with no match we fall back to the
      // cheapest overall, as before.
      _validSkuId =
        findStoreLanguageSkuId(
          streamlinedAvailableVariants,
          settings.language,
        ) ??
        streamlinedAvailableVariants?.[0]?.skuId ??
        null;
    }

    return _validSkuId;
  };

  const validSkuId = getMatchingSkuId();

  // Additional Info
  const additionalInfo: AdditionalInfoT = {
    conditions: null,
    reviews: null,
    breadcrumb: null,
    categoryWarrantyInfo: null,
    warrantyExcludedPskus: null,
  };

  // TODO: To be moved to the Client Side for performance improvement
  const categoryPk = product?.category?.pk ?? '';
  // Conditions are keyed by reebeloId; warranty and breadcrumb are still pk-based.
  const categoryId = product?.category?.id ?? '';

  const [categoryConditionContent, categoryWarrantyInfo, breadcrumb, reviews] =
    await Promise.all([
      categoryApi.fetchCategoryConditionContent(categoryId, settings.store),
      categoryApi.fetchWarrantyInfoByCategoryPk(categoryPk, settings.store),
      categoryApi.fetchBreadcrumb(categoryPk, settings.store),
      getProductReviews({ psku: product.psku, batchSize: 4 }),
    ]);

  additionalInfo.conditions = categoryConditionContent?.options ?? null;
  additionalInfo.reviews = reviews;
  additionalInfo.breadcrumb = streamlineBreadcrumb(breadcrumb, categoryPk);
  additionalInfo.categoryWarrantyInfo = categoryWarrantyInfo;

  // Fetch excluded PSKUs from SSM and attach to additionalInfo
  const warrantyExcludedPskusValue = await ssm.getStoreValue<string>(
    settings.store,
    'warranty_excluded_pskus',
  );

  additionalInfo.warrantyExcludedPskus =
    warrantyExcludedPskusValue?.split(',') ?? [];

  const screenSizeFamily = getScreenSizeFamily(product.psku);

  if (screenSizeFamily) {
    const siblings = await catalog
      .getProducts({
        pskus: screenSizeFamily.map((member) => member.psku),
        limit: screenSizeFamily.length,
        includeFilters: false,
      })
      .catch(() => ({ items: [] as ProductT[] }));

    const slugByPsku = new Map(
      (siblings.items ?? []).map((item) => [item.psku, item.slug]),
    );

    const options = screenSizeFamily.flatMap((member) => {
      const slug = slugByPsku.get(member.psku);

      return slug
        ? [
            {
              screenSize: member.screenSize,
              slug,
              isSelected: member.psku === product.psku,
            },
          ]
        : [];
    });

    additionalInfo.screenSizeOptions = options.length > 1 ? options : [];
  }

  // Prefetch Selected Sku Data (to be used in Client Side)
  const selectedSku = await catalog
    .getSkuById(validSkuId, 'storefront')
    .catch(() => null);

  // The SKU comes back with the whole product nested inside it, which we
  // already ship at the top level — 4 KB of duplicate JSON in every response.
  // Its offers each carry a delivery estimate for every state and region the
  // vendor ships to, another 48 KB of which the page quotes two rows.
  const streamlineSku = (sku: SkuT | null): SkuT | null => {
    if (!sku) {
      return null;
    }

    const stripped = omit(sku, 'product') as SkuT;

    return {
      ...stripped,
      offers: trimOfferDeliveryEstimates(stripped.offers, destination),
    };
  };

  // Props
  const props: ProductPageProps = {
    productId,
    skuId: validSkuId,
    product: {
      ...product,
      description: interpolateStoreTokens(product.description ?? ''),
      variants: streamlinedProductVariants,
    },
    availableVariants: packAvailableVariants(streamlinedAvailableVariants),
    additionalInfo,
    selectedSku: streamlineSku(selectedSku),
    msCatalogEnabled: true,
    type: 'MS_CATALOG_PRODUCT_PAGE',
  };

  return {
    props,
  };
};

const getMsCatalogCollectionPageProps = async ({
  context,
  handle,
}: {
  context: GetServerSidePropsContext<CollectionsParams>;
  handle: string;
}) => {
  const productGroupApi = new ProductGroupApi();

  const collection = await productGroupApi.fetchGroupByHandle(
    encodeURIComponent(handle as string),
    settings.store,
  );

  if (!collection || (collection && !collection.isActive)) {
    return NOT_FOUND;
  }

  // Fixed Filters
  const fixedFilters: SearchProductsParamsT = {};

  // Categories
  if (collection?.filters?.categories) {
    fixedFilters.categories = collection.filters.categories;
  }

  // Brands
  if (collection?.filters?.brand) {
    fixedFilters.brands = collection.filters.brand;
  }

  // PSKUs
  if (collection?.filters?.psku) {
    fixedFilters.pskus = collection.filters.psku;
  }

  const { products, total, filters, searchParams } = await fetchProducts({
    urlParams: context.query,
    fixedFilters,
  });

  // Drop the product fields the page doesn't render to keep
  // __NEXT_DATA__ small.
  const streamlineProduct = (product: ProductT, index: number): ProductT => {
    const sku = product.skus?.[0];
    const offer = sku?.offers?.[0];

    return {
      ...pick(product, ['id', 'slug', 'name', 'title', 'image']),
      // The Open Graph tags list every image of the first 5 products.
      images:
        index < 5 ? product.images ?? [] : product.images?.slice(0, 1) ?? [],
      skus:
        sku && offer
          ? [
              {
                ...pick(sku, ['id', 'image']),
                images: sku.images?.slice(0, 1) ?? [],
                offers: [pick(offer, ['price'])],
              },
            ]
          : [],
    } as ProductT;
  };

  const props: CollectionPageProps = {
    products: products.map(streamlineProduct),
    spotlightedProducts: null, // Exclusive for Deals Collection Page
    filters,
    total,
    collectionName: collection.name,
    breadcrumb: null, // Exclusive for Category Collection Pages
    pk: null, // Exclusive for Category Collection Pages
    fixedFilters,
    searchParams,
    handle,
    msCatalogEnabled: true,
    type: 'MS_CATALOG_NAV_COLLECTION_PAGE',
  };

  return {
    props,
  };
};

const getMsCatalogProps: GetServerSideProps<
  ProductPageProps | CollectionPageProps,
  CollectionsParams
> = async (context: GetServerSidePropsContext<CollectionsParams>) => {
  const productId: string | null = context.params?.handle
    ? (context.params?.handle as string)
    : null;

  const skuId = context.query?.skuId
    ? decodeURIComponent(context.query.skuId as string)
    : null;

  if (!productId) {
    return REDIRECT_HOME;
  }

  const newProductId = DEPRECATED_HANDLES_MAP[productId];

  if (newProductId) {
    return PERMANENT_REDIRECT_ROUTE({ handle: newProductId });
  }

  const catalog = new CatalogV2Api(settings.store, {
    channel: ProductChannelEnum.STOREFRONT,
  });

  const product = await catalog
    .getProductByProductId(encodeURIComponent(productId))
    .catch((err) => {
      // Only a 404 means "not a product, try the collections". Any other failure
      // surfaces as a 500 so an outage never reads as the page being gone.
      if (isNotFoundError(err)) {
        return null;
      }

      throw err;
    });

  // If product exists, render Product Page
  if (product) {
    const queryParams = context.query;

    return getMsCatalogProductPageProps({
      product,
      productId,
      skuId,
      catalog,
      queryParams,
      destination: resolveDeliveryDestination(context.req.cookies),
    });
  }

  return getMsCatalogCollectionPageProps({ context, handle: productId });
};

export const getServerSideProps: GetServerSideProps<
  any,
  CollectionsParams
> = async (event: GetServerSidePropsContext<CollectionsParams>) => {
  const rawHandle = event.query.handle as string | undefined;

  if (!rawHandle) {
    return REDIRECT_HOME;
  }

  return getMsCatalogProps(event);
};

export default function CollectionPages(props: Props) {
  if (props.type === 'MS_CATALOG_PRODUCT_PAGE') {
    return (
      <ProductPageProvider props={props} key={props.productId}>
        <ProductPageLayout />
      </ProductPageProvider>
    );
  }

  if (props.type === 'MS_CATALOG_NAV_COLLECTION_PAGE') {
    return (
      <CollectionPageProvider props={props} key={props.handle}>
        <CollectionPageLayout />
      </CollectionPageProvider>
    );
  }

  return null;
}
